import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { PillView, useAction } from "../../../components/ui";
import { date, money as moneyFmt } from "../../../lib/format";
import { KampagnenDialog, geklappt } from "./bausteine";
import { ordneKampagneZu } from "./daten";
import { useHeute, useKooperationen } from "./hooks";
import { baueZeilen, kampagnenStand, pill, sammelMonate } from "./model";
import { useQuery } from "@tanstack/react-query";
import { BerichtBereich } from "./Marke";
import { ladeBerichteDerKampagne, ladeKontakte, ladeMarkenEinstellungen } from "./marke-daten";
import { t } from "../../../lib/sprache";

/**
 * Kampagne als Klammer über mehrere Kooperationen derselben Marke (Befund 22): gemeinsames Briefing, Budget und Deadline,
 * die Kooperationen der Creator mit Stand und Frist, und die Sammelrechnung an die Marke über alle offenen Monate.
 */
const STAND = { get active() { return pill(t("Läuft", "Running"), "info"); }, get done() { return pill(t("Abgeschlossen", "Completed"), "success"); }, get cancelled() { return pill(t("Abgesagt", "Declined"), "neutral"); } } as const;

export function Kampagne() {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const { campaignId } = useParams();
  const ws = useStaffWorkspace();
  const q = useKooperationen();
  const today = useHeute();
  const { busy, error, run } = useAction();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [zuordnen, setZuordnen] = useState("");
  const zeilen = useMemo(() => (q.data ? baueZeilen(q.data, { today, timeZone: ws.org.timezone, selbst: !!ws.isCreator }) : []), [q.data, today, ws.org.timezone]);
  // 0073: Berichte für die Marke über alle Kooperationen der Kampagne
  const markenBrand = q.data?.campaigns.find((c) => c.id === campaignId)?.brand_id;
  const berichte = useQuery({
    queryKey: ["brand_reports", ws.org.org_id, "kampagne", campaignId, markenBrand],
    enabled: !!campaignId && !!markenBrand,
    queryFn: async () => {
      const [reports, contacts, settings] = await Promise.all([ladeBerichteDerKampagne(api, campaignId!), ladeKontakte(api, markenBrand!), ladeMarkenEinstellungen(api, ws.org.org_id)]);
      return { reports, contacts, settings };
    },
  });

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const kampagne = q.data.campaigns.find((c) => c.id === campaignId);
  if (!kampagne) {
    return (
      <main id="main" className="content tk-koop">
        <div className="panel stack tk-empty" role="alert">
          <h1 className="tk-state-title">{t("Diese Kampagne gibt es nicht oder sie gehört zu einer anderen Agentur.", "This campaign does not exist or belongs to another agency.")}</h1>
          <Link className="btn" to="/agentur/kooperationen/nach-marke">{t("Kooperationen nach Marke", "Deals by brand")}</Link>
        </div>
      </main>
    );
  }
  const k = kampagnenStand(kampagne, zeilen);
  const marke = q.data.brands.find((b) => b.id === kampagne.brand_id);
  // Nur laufende Kooperationen der Marke in derselben Währung
  const frei = zeilen.filter((z) => z.deal.brand_id === kampagne.brand_id && !z.deal.campaign_id && !z.archiviert && !z.status.erledigt && z.deal.currency === kampagne.currency);
  const offen = sammelMonate(zeilen, { brandId: kampagne.brand_id, currency: kampagne.currency, campaignId: kampagne.id });
  const offenSumme = offen.reduce((a, m) => a + m.betragCents + m.extrasCents, 0);
  const ueberfaellig = kampagne.deadline && kampagne.deadline < today && kampagne.status === "active";

  return (
    <main id="main" className="content tk-koop">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Kampagne · ", "Campaign · ")}{marke?.name ?? "–"}</div>
          <h1>{kampagne.title}</h1>
          <p>{k.zeilen.length === 1 ? t("1 Kooperation", "1 deal") : t(`${k.zeilen.length} Kooperationen`, `${k.zeilen.length} deals`)} · {k.creator === 1 ? t("1 Creator", "1 creator") : t(`${k.creator} Creator`, `${k.creator} creators`)}{kampagne.deadline ? t(` · Deadline ${date(kampagne.deadline)}`, ` · deadline ${date(kampagne.deadline)}`) : ""}</p>
        </div>
        <div className="row">
          <button onClick={() => setBearbeiten(true)}>{t("Bearbeiten", "Edit")}</button>
          <Link className="btn" to={`/agentur/kooperationen/neu?kampagne=${kampagne.id}`}>{t("Kooperation hinzufügen", "Add deal")}</Link>
          <Link className="btn primary" to={`/agentur/rechnungen/neu?sammel=1&marke=${kampagne.brand_id}&kampagne=${kampagne.id}`}>{t("Sammelrechnung erstellen", "Create collective invoice")}</Link>
        </div>
      </div>
      <div className="row">
        <PillView pill={STAND[kampagne.status]} />
        {ueberfaellig && <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Deadline überschritten", "Deadline passed")}</span>}
      </div>
      <div className="split">
        <div className="stack">
          <section className="panel stack">
            <h3>Briefing</h3>
            {kampagne.briefing ? <p className="tk-koop-vorlauf">{kampagne.briefing}</p> : <p className="muted">{t("Noch kein Briefing. Über „Bearbeiten“ eintragen; es gilt für alle Creator der Kampagne.", "No briefing yet. Enter it via “Edit”; it applies to all creators of the campaign.")}</p>}
          </section>
          <section className="panel stack">
            <h3>{t("Abrechnung an die Marke", "Invoicing to the brand")}</h3>
            <p>{offen.length ? t(`${offen.length === 1 ? t("1 Monat", "1 month") : t(`${offen.length} Monate`, `${offen.length} months`)} ohne Rechnung an die Marke, zusammen ${money(offenSumme, kampagne.currency)} netto.`, `${offen.length === 1 ? t("1 Monat", "1 month") : t(`${offen.length} Monate`, `${offen.length} months`)} without an invoice to the brand, together ${money(offenSumme, kampagne.currency)} net.`) : t("Alle Monate mit Honorar sind an die Marke abgerechnet.", "All months with a fee have been invoiced to the brand.")}</p>
            {offen.length > 0 && <div className="row"><Link className="btn primary" to={`/agentur/rechnungen/neu?sammel=1&marke=${kampagne.brand_id}&kampagne=${kampagne.id}`}>{t("Sammelrechnung erstellen", "Create collective invoice")}</Link></div>}
            <p className="source-note">{t("Eine Sammelrechnung fasst mehrere Monate und Creator zusammen; jede Position nennt Kooperation und Monat, und jeder Monat gilt danach als abgerechnet. Kooperationen mit dem Abrechnungsweg „direkt an die Marke“ rechnen die Creator selbst ab.", "A collective invoice combines several months and creators; each item names deal and month, and each month then counts as invoiced. Deals with the invoicing route “directly to the brand” are invoiced by the creators themselves.")}</p>
          </section>
        </div>
        <div className="stack">
          <section className="panel stack">
            <h3>Budget</h3>
            <div className="task"><span>{t("Budget der Kampagne", "Campaign budget")}</span><strong className="num">{kampagne.budget_cents == null ? "–" : money(kampagne.budget_cents, kampagne.currency)}</strong></div>
            <div className="task"><span>{t("Verplant (Honorare der Kooperationen)", "Allocated (deal fees)")}</span><strong className="num">{money(k.honorarCents, kampagne.currency)}</strong></div>
            {k.restCents != null && <div className="task"><span>{k.restCents < 0 ? t("Über Budget", "Over budget") : t("Noch frei", "Still available")}</span><strong className="num">{money(Math.abs(k.restCents), kampagne.currency)}</strong></div>}
            {k.restCents != null && k.restCents < 0 && <p className="notice">{t("Die Honorare liegen über dem Budget der Kampagne.", "The fees exceed the campaign budget.")}</p>}
          </section>
        </div>
      </div>
      <section className="panel stack">
        <h3>{t("Kooperationen", "Deals")}</h3>
        {k.zeilen.length === 0 && <p className="muted">{t("Noch keine Kooperation in dieser Kampagne.", "No deal in this campaign yet.")}</p>}
        {k.zeilen.length > 0 && (
          <div className="tablewrap tk-koop-karten tk-koop-schmal">
            <table>
              <caption className="sr-only">{t("Kooperationen der Kampagne", "Deals of the campaign")}</caption>
              <thead><tr><th scope="col">Creator</th><th scope="col">{t("Kooperation", "Deal")}</th><th scope="col">Status</th><th scope="col">{t("Nächste Frist", "Next deadline")}</th><th scope="col" className="money">{t("Honorar netto", "Net fee")}</th><th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th></tr></thead>
              <tbody>
                {k.zeilen.map((z) => (
                  <tr key={z.deal.id}>
                    <td className="tk-koop-kopf"><Link to={`/agentur/kooperationen/${z.deal.id}`}><strong>{z.talentName}</strong></Link></td>
                    <td data-label={t("Kooperation", "Deal")}>{z.deal.title}<small>{z.modell}</small></td>
                    <td data-label="Status"><PillView pill={z.status.pill} /></td>
                    <td data-label={t("Nächste Frist", "Next deadline")}>{z.frist ? `${date(z.frist.date)} · ${z.frist.art}${z.frist.ueberfaellig ? t(" · überfällig", " · overdue") : ""}` : "–"}</td>
                    <td data-label={t("Honorar netto", "Net fee")} className="num">{z.honorarCents == null ? "–" : money(z.honorarCents, z.deal.currency)}</td>
                    <td className="tk-koop-aktion">
                      <button disabled={busy !== null} onClick={() => void run("raus", () => geklappt(ordneKampagneZu(api, [z.deal.id], null)), t(`${z.talentName} ist nicht mehr Teil der Kampagne.`, `${z.talentName} is no longer part of the campaign.`))}>{t("Aus Kampagne nehmen", "Remove from campaign")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {frei.length > 0 && (
          <div className="row">
            <label>
              {t("Bestehende Kooperation zuordnen", "Assign existing deal")}<select value={zuordnen} onChange={(e) => setZuordnen(e.target.value)}>
                <option value="">{t("Kooperation wählen …", "Choose deal …")}</option>
                {frei.map((z) => <option key={z.deal.id} value={z.deal.id}>{z.talentName} · {z.deal.title}</option>)}
              </select>
            </label>
            <button disabled={!zuordnen || busy !== null}
              onClick={() => void run("zuordnen", () => geklappt(ordneKampagneZu(api, [zuordnen], kampagne.id)), t("Kooperation zugeordnet.", "Deal assigned.")).then((r) => r && setZuordnen(""))}>
              {t("Zuordnen", "Assign")}</button>
          </div>
        )}
        {error && <p className="tk-error" role="alert">{error}</p>}
      </section>
      {berichte.data && (
        <BerichtBereich orgId={ws.org.org_id} brandName={marke?.name ?? null} dealId={null} campaignId={kampagne.id} reports={berichte.data.reports}
          contacts={berichte.data.contacts} tage={berichte.data.settings?.brand_report_link_days ?? 30} archiviert={false} brandLanguage={marke?.language ?? null} />
      )}
      {bearbeiten && <KampagnenDialog open onClose={() => setBearbeiten(false)} kampagne={kampagne} />}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}
